import React, { useState, useEffect } from "react";
import "./HomePane.css";
import Joyride, { STATUS } from "react-joyride";
import CountUp from "react-countup";
import { FaQuestionCircle, FaBug } from "react-icons/fa";
import HomeZoomPaneModal from "./HomeZoomPaneModal";
import NumberOfStudentsInEachGroupPane from "./SubPanes/NumberOfStudentsInEachGroupPane";
import TrendPane from "./SubPanes/TrendPane";
import AttendancePane from "./SubPanes/AttendancePane";
import DynamicUpdatesPane from "./SubPanes/DynamicUpdatesPane";
import UXFeedbackModal from "./UXFeedback/UXFeedbackModal";
import UXFeedbackInviteModal from "./UXFeedback/UXFeedbackInviteModal";
import CustomModal from "../../../Global/Modal/CustomModal";

// Memoized DashboardCard component
const DashboardCard = React.memo(
  ({
    title,
    description,
    flex,
    height = "150px",
    children,
    "data-tour": dataTour,
    onClick,
  }) => (
    <div
      className="dashboard-card"
      style={{
        flex: `${flex}`,
        direction: "rtl",
        minHeight: height,
        height: "auto",
        cursor: onClick ? "pointer" : "default",
        margin: "0px",
      }}
      data-tour={dataTour}
      onClick={(e) => {
        // Check if the click originated from a select element or its parent
        if (e.target.tagName === "SELECT" || e.target.closest("select")) {
          return;
        }
        onClick?.();
      }}
    >
      <p
        className="secular-one-regular subtitle"
        style={{ margin: 0, fontSize: "1.1em", direction: "rtl" }}
      >
        {title}
      </p>
      <p
        className="normal-text"
        style={{ marginTop: "4px", marginBottom: "12px", fontSize: "0.9em" }}
      >
        {typeof description === "function" ? description() : description}
      </p>
      <div
        className="dashboard-card-content"
        style={{ height: "calc(100% - 60px)" }}
      >
        {children}
      </div>
    </div>
  )
);

function HomePane({
  userData,
  students,
  groups,
  setCurrentTab,
  presenceLogs,
  professionalContent,
  statsReports,
  events,
  isNavTourRunning,
}) {
  // --- MODERN DESIGN STYLES (from GroupsPane) ---
  const mainBg = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%)",
    padding: "32px 0",
  };
  const containerStyle = {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 16px",
  };
  // --- END MODERN DESIGN STYLES ---
  const [selectedGroupForAttendance, setSelectedGroupForAttendance] =
    useState("all");
  const [runTour, setRunTour] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [showUXFeedback, setShowUXFeedback] = useState(false);
  const [showFeedbackInvite, setShowFeedbackInvite] = useState(false);
  const [birthdayModalOpen, setBirthdayModalOpen] = useState(false);
  const [waveringModalOpen, setWaveringModalOpen] = useState(false);
  const [trainingPlanModalOpen, setTrainingPlanModalOpen] = useState(false);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [waveringStudents, setWaveringStudents] = useState([]);

  // Calculate attendance map whenever presenceLogs or students change
  useEffect(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);

    const newAttendanceMap = {};
    students.forEach((student) => {
      if (!student.groupsIDs || student.groupsIDs.length === 0) return;
      let total = 0;
      let missed = 0;
      student.groupsIDs.forEach((groupID) => {
        // Get all presence logs for this group in the last 30 days
        const groupLogs = presenceLogs.filter((log) => {
          let logTime;
          if (log.logTime && typeof log.logTime.toDate === "function") {
            logTime = log.logTime.toDate();
          } else if (log.logTime && log.logTime.seconds) {
            logTime = new Date(log.logTime.seconds * 1000);
          } else {
            logTime = log.logTime;
          }
          return log.groupID === groupID && logTime && logTime >= thirtyDaysAgo;
        });
        groupLogs.forEach((log) => {
          const presentMap = Array.isArray(log.presenceLogDataForFront)
            ? log.presenceLogDataForFront
            : [];
          const entry = presentMap.find((e) => e.studentID === student.id);
          if (entry) {
            total++;
            if (!entry.isPresent) {
              missed++;
            }
          }
        });
      });
      if (total > 0) {
        newAttendanceMap[student.id] = { present: total - missed, total };
      }
    });
    setAttendanceMap(newAttendanceMap);
  }, [presenceLogs, students]);

  // Calculate wavering students whenever presenceLogs or students change
  useEffect(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);

    const newWaveringStudents = students.filter((student) => {
      if (!student.groupsIDs || student.groupsIDs.length === 0) return false;
      let totalLogs = 0;
      let missedLogs = 0;

      student.groupsIDs.forEach((groupID) => {
        // Get all presence logs for this group in the last 30 days
        const groupLogs = presenceLogs.filter((log) => {
          let logTime;
          if (log.logTime && typeof log.logTime.toDate === "function") {
            logTime = log.logTime.toDate();
          } else if (log.logTime && log.logTime.seconds) {
            logTime = new Date(log.logTime.seconds * 1000);
          } else {
            logTime = log.logTime;
          }
          return log.groupID === groupID && logTime && logTime >= thirtyDaysAgo;
        });

        groupLogs.forEach((log) => {
          const presentMap = Array.isArray(log.presenceLogDataForFront)
            ? log.presenceLogDataForFront
            : [];
          const entry = presentMap.find((e) => e.studentID === student.id);
          if (entry) {
            totalLogs++;
            if (!entry.isPresent) {
              missedLogs++;
            }
          }
        });
      });

      // Only consider students with at least one log
      if (totalLogs === 0) return false;
      return missedLogs / totalLogs > 0.5;
    });

    setWaveringStudents(newWaveringStudents);
  }, [presenceLogs, students]);

  // Generate fake data for tour guide
  const generateFakeData = () => {
    // Fake groups data
    const fakeGroups = [
      { id: "group1", name: "קבוצת מתחילים", dojo: "תל אביב" },
      { id: "group2", name: "קבוצת ביניים", dojo: "תל אביב" },
      { id: "group3", name: "קבוצת מתקדמים", dojo: "ירושלים" },
      { id: "group4", name: "קבוצת ילדים", dojo: "ירושלים" },
    ];

    // Fake students data for demographics
    const fakeStudents = [
      {
        gender: "זכר",
        birthday: { toDate: () => new Date(2010, 0, 1) },
        groupsIDs: ["group1"],
      },
      {
        gender: "נקבה",
        birthday: { toDate: () => new Date(2012, 0, 1) },
        groupsIDs: ["group1"],
      },
      {
        gender: "זכר",
        birthday: { toDate: () => new Date(2014, 0, 1) },
        groupsIDs: ["group2"],
      },
      {
        gender: "נקבה",
        birthday: { toDate: () => new Date(2016, 0, 1) },
        groupsIDs: ["group2"],
      },
      {
        gender: "זכר",
        birthday: { toDate: () => new Date(2018, 0, 1) },
        groupsIDs: ["group3"],
      },
      {
        gender: "נקבה",
        birthday: { toDate: () => new Date(2020, 0, 1) },
        groupsIDs: ["group4"],
      },
    ];

    // Fake presence logs
    const fakePresenceLogs = [
      {
        logTime: { toDate: () => new Date() },
        totalmissingStudents: 2,
        totalPresentStudents: 15,
        groupID: "all",
      },
      {
        logTime: { toDate: () => new Date() },
        totalmissingStudents: 1,
        totalPresentStudents: 18,
        groupID: "all",
      },
    ];

    // Fake stats reports - specific dates starting from May 15, 2025
    const getTimestampForMonth = (monthsAgo) => {
      const baseDate = new Date(2025, 4, 15, 12, 0, 0); // May 15, 2025, 12:00:00
      const date = new Date(baseDate);
      date.setMonth(date.getMonth() - monthsAgo);
      return Math.floor(date.getTime() / 1000);
    };

    const fakeStatsReports = [
      {
        timestamp: { seconds: getTimestampForMonth(0) },
        totalStudentsInGroups: 45,
      }, // May 15, 2025
      {
        timestamp: { seconds: getTimestampForMonth(1) },
        totalStudentsInGroups: 40,
      }, // Apr 15, 2025
      {
        timestamp: { seconds: getTimestampForMonth(2) },
        totalStudentsInGroups: 65,
      }, // Mar 15, 2025
      {
        timestamp: { seconds: getTimestampForMonth(3) },
        totalStudentsInGroups: 62,
      }, // Feb 15, 2025
      {
        timestamp: { seconds: getTimestampForMonth(4) },
        totalStudentsInGroups: 88,
      }, // Jan 15, 2025
      {
        timestamp: { seconds: getTimestampForMonth(5) },
        totalStudentsInGroups: 75,
      }, // Dec 15, 2024
    ];

    // Fake important numbers
    const fakeImportantNumbers = {
      numberOfStudents: 154,
      numberOfGroups: 10,
      numberOfTrainingInLastMonth: 40,
    };

    return {
      groups: fakeGroups,
      students: fakeStudents,
      presenceLogs: fakePresenceLogs,
      statsReports: fakeStatsReports,
      importantNumbers: fakeImportantNumbers,
    };
  };

  // Get appropriate greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return "בוקר טוב";
    if (hour >= 12 && hour < 17) return "צהריים טובים";
    if (hour >= 17 && hour < 21) return "ערב טוב";
    return "לילה טוב";
  };

  // Returns number of groups
  const getNumberOfGroups = () => {
    return groups.length;
  };

  // Return number of students (which are associated with an active group and have status 'active')
  const getNumberOfStudent = () => {
    const activeGroupsIDs = groups.map((group) => group.id);
    const studentsInActiveGroups = students.filter(
      (student) =>
        student?.status === "active" &&
        student?.groupsIDs?.length > 0 &&
        activeGroupsIDs.includes(student.groupsIDs[0])
    ).length;
    return studentsInActiveGroups;
  };

  // Return the number of training sessions that occured in the previous month, using the presence logs
  const getNumberOfTrainingInLastMonth = () => {
    // Get the current date and the date one month ago
    const now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(now.getMonth() - 1); // Subtract 1 month from the current date

    // Filter the presence logs to only include those within the last month
    const logsInLastMonth = presenceLogs.filter((log) => {
      const logTime = log.logTime?.toDate(); // Convert Firebase timestamp to JavaScript Date
      return logTime >= oneMonthAgo;
    });

    // Return the count of logs in the last month
    return logsInLastMonth.length;
  };

  // Due to the fact the dashboard is actually not dynamid, there is no need to use states in this file, I use simple variables.
  const fakeData = runTour ? generateFakeData() : null;
  const numberOfGroups = runTour
    ? fakeData.importantNumbers.numberOfGroups
    : getNumberOfGroups();
  const numberOfStudents = runTour
    ? fakeData.importantNumbers.numberOfStudents
    : getNumberOfStudent();
  const numberOfTrainingInLastMonth = runTour
    ? fakeData.importantNumbers.numberOfTrainingInLastMonth
    : getNumberOfTrainingInLastMonth();

  // Define tour steps
  const steps = [
    {
      target: ".dashboard-wrapper",
      content:
        "ברוכים הבאים לדשבורד הבקרה! כאן תוכלו לראות את כלל הנתונים החשובים על הארגון כולל סטטיסטיקות וגרפים שיעזרו לכם לשלוט בארגון. תרצו שנעשה לכם סיור קצר?",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="stats-pane"]',
      content:
        "כאן מרוכזים נתוני העל של הארגון - כמות הספורטאים והקבוצות, סך כל האימונים שבוצעו החודש תוך התבססות על דיווחי הנוכחות בארגון, וחישוב ממוצע של כמות הספורטאים לקבוצה",
      placement: "right",
      disableScrolling: true,
    },
    {
      target: '[data-tour="groups-pane"]',
      content: `כאן תוכלו לצפות בכמות הספורטאים שמתאמנים בכל קבוצה. ניתן לסנן את המידע לפי סניף בשביל לצפות במידע בצורה נוחה יותר!`,
      placement: "left",
      disableScrolling: true,
    },
    {
      target: '[data-tour="trend-pane"]',
      content:
        "כאן תוכלו לצפות במגמת השינוי של כמות הספורטאים בארגון בחודשים האחרונים. המידע מתעדכן אחת לחודש, ומציג את הנתונים עד שנה אחורה.",
      placement: "top",
      disableScrolling: true,
    },
    {
      target: '[data-tour="attendance-pane"]',
      content:
        "כאן תוכלו לצפות בכמות ההיעדרויות שהתרחשו בחודש האחרון, ולסנן את המידע לפי קבוצה.",
      placement: "left",
      disableScrolling: true,
    },
    {
      target: '[data-tour="dynamic-updates-pane"]',
      content: "כאן תוכלו לצפות בעדכונים דינמיים על מאפייני הספורטאים בארגון",
      placement: "right",
      disableScrolling: true,
    },
    {
      target: '[title="הפעל סיור מודרך"]',
      content: "אם תרצו לראות את הסיור שוב, תוכלו ללחוץ על כפתור זה בכל עת!",
      placement: "right",
      spotlightClicks: true,
    },
  ];

  // Remove all localStorage logic from handleJoyrideCallback
  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
    }
  };

  // Add CSS for the tour button
  const tourButtonStyle = {
    position: "absolute",
    top: "20px",
    left: "20px",
    backgroundColor: "#0066CC",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    zIndex: 100,
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  };

  const handleTourButtonClick = () => {
    setRunTour(true);
  };

  const handleUXFeedbackButtonClick = () => {
    // Don't show UX feedback modal if nav tour is running
    if (!isNavTourRunning) {
      setShowUXFeedback(true);
    }
  };

  // Add CSS for the UX feedback button
  const uxFeedbackButtonStyle = {
    position: "absolute",
    top: "20px",
    left: "70px", // Positioned next to the tour button
    backgroundColor: isNavTourRunning ? "#cccccc" : "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: isNavTourRunning ? "not-allowed" : "pointer",
    transition: "all 0.3s ease",
    zIndex: 100,
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    opacity: isNavTourRunning ? 0.6 : 1,
  };

  const locale = {
    back: "הקודם",
    close: "סגור",
    last: "סיום",
    next: "הבא",
    skip: "דלג",
  };

  // Check if we should show the feedback invite
  useEffect(() => {
    // Only check if we're not already showing the invite and nav tour is not running
    if (!showFeedbackInvite && !showUXFeedback && !isNavTourRunning) {
      // Generate a random number between 1 and 5
      const randomNumber = Math.floor(Math.random() * 5) + 1;

      // Show the invite 1 in 5 times (20% chance) when randomNumber is 1
      if (randomNumber === 1 && localStorage.getItem("hasSeenDashboardTour")) {
        setShowFeedbackInvite(true);
      }
    }
  }, [isNavTourRunning]); // Add isNavTourRunning to dependencies

  const handleFeedbackInviteAccept = () => {
    setShowFeedbackInvite(false);
    setShowUXFeedback(true);
  };

  const handleFeedbackInviteClose = () => {
    setShowFeedbackInvite(false);
  };

  const openBirthdayModal = () => setBirthdayModalOpen(true);
  const closeBirthdayModal = () => setBirthdayModalOpen(false);
  const openWaveringModal = () => setWaveringModalOpen(true);
  const closeWaveringModal = () => setWaveringModalOpen(false);
  const openTrainingPlanModal = () => setTrainingPlanModalOpen(true);
  const closeTrainingPlanModal = () => setTrainingPlanModalOpen(false);

  // Birthday logic for modal
  const getUpcomingBirthdays = () => {
    const today = new Date();
    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(today.getDate() + 14);
    return students
      .filter((student) => {
        if (!student.birthday) return false;
        const birthday = student.birthday.toDate();
        const birthdayThisYear = new Date(
          today.getFullYear(),
          birthday.getMonth(),
          birthday.getDate()
        );
        return birthdayThisYear >= today && birthdayThisYear <= twoWeeksFromNow;
      })
      .sort((a, b) => {
        const aBirthday = a.birthday.toDate();
        const bBirthday = b.birthday.toDate();
        const aBirthdayThisYear = new Date(
          today.getFullYear(),
          aBirthday.getMonth(),
          aBirthday.getDate()
        );
        const bBirthdayThisYear = new Date(
          today.getFullYear(),
          bBirthday.getMonth(),
          bBirthday.getDate()
        );
        return aBirthdayThisYear - bBirthdayThisYear;
      });
  };

  const getDaysUntilBirthday = (birthday) => {
    const today = new Date();
    const birthdayDate = birthday.toDate();
    const birthdayThisYear = new Date(
      today.getFullYear(),
      birthdayDate.getMonth(),
      birthdayDate.getDate()
    );
    const diffTime = birthdayThisYear - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getNextAge = (birthday) => {
    const today = new Date();
    const birthdayDate = birthday.toDate();
    return today.getFullYear() - birthdayDate.getFullYear();
  };

  // Training plan related functions
  const getCurrentMonthAndWeek = () => {
    const now = new Date();
    const currentYear = now.getFullYear().toString();
    const currentMonth = (now.getMonth() + 1).toString(); // Remove padding, use "1" not "01"
    const currentWeek = Math.ceil(now.getDate() / 7);

    const yearData = professionalContent?.years?.[0]?.[currentYear];
    const monthData = yearData?.[currentMonth];
    const weekData = monthData?.weeks?.[currentWeek];

    return {
      year: currentYear,
      month: currentMonth,
      week: currentWeek,
      monthData,
      weekData,
    };
  };

  const getMonthName = (monthNum) => {
    const months = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];
    return months[parseInt(monthNum) - 1] || monthNum;
  };

  return (
    <>
      <style>{`
        .modern-home-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .modern-home-card:hover {
          box-shadow: 0 4px 24px #1976d233 !important;
          transform: translateY(-2px) scale(1.01);
        }
        .modern-section-header-icon {
          font-size: 1.3em;
          margin-left: 8px;
        }
        .modern-modal .modal-content {
          border-radius: 18px !important;
          background: #f8fafc !important;
        }
        .modern-modal .modal-header {
          border-radius: 18px 18px 0 0 !important;
        }
        .modern-modal .modal-footer {
          border-radius: 0 0 18px 18px !important;
        }
        .modern-form-input, .modern-form-input:focus {
          border-radius: 10px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          border: 1px solid #e0e0e0 !important;
          background: #fff !important;
        }
      `}</style>
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#0066CC",
            textColor: "#333",
            zIndex: 1000,
          },
          tooltipContainer: {
            direction: "rtl",
            textAlign: "right",
          },
          buttonNext: {
            backgroundColor: "#0066CC",
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonBack: {
            marginRight: 10,
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonSkip: {
            color: "#666",
            fontSize: 14,
            padding: "8px 16px",
          },
        }}
        locale={locale}
        floaterProps={{
          disableAnimation: true,
          styles: {
            floater: {
              direction: "rtl",
            },
          },
        }}
        tooltipComponent={null}
      />
      <div style={mainBg}>
        <div style={containerStyle}>
          <div
            className="dashboard-wrapper"
            style={{ paddingRight: "2vw", margin: "0px", paddingTop: "3vh" }}
          >
            <button
              style={tourButtonStyle}
              onClick={handleTourButtonClick}
              title="הפעל סיור מודרך"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.1)";
                e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
              }}
            >
              <FaQuestionCircle size={20} />
            </button>
            <button
              style={uxFeedbackButtonStyle}
              onClick={handleUXFeedbackButtonClick}
              title={isNavTourRunning ? "לא זמין במהלך הסיור" : "דיווח על בעיה"}
              onMouseEnter={(e) => {
                if (!isNavTourRunning) {
                  e.currentTarget.style.transform = "scale(1.1)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isNavTourRunning) {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
                }
              }}
            >
              <FaBug size={20} />
            </button>
            <p className="secular-one-regular title">
              {getGreeting()}, {userData?.userName}
            </p>
            <div className="pane-row-wrapper" style={{ marginBottom: "20px" }}>
              <DashboardCard
                title={"ספורטאים בקבוצות"}
                description={"כמה ספורטאים יש בכל קבוצה"}
                flex={2}
                data-tour="groups-pane"
                onClick={() => setActiveModal("groups")}
                className="modern-home-card"
              >
                <NumberOfStudentsInEachGroupPane
                  groups={runTour ? fakeData.groups : groups}
                  students={runTour ? fakeData.students : students}
                  isModal={false}
                />
              </DashboardCard>
              <DashboardCard
                title={"נוכחות באימונים"}
                description={"כמה פעמים ספורטאים נעדרו מאימונים בחודש האחרון?"}
                flex={1}
                data-tour="attendance-pane"
                onClick={() => setActiveModal("attendance")}
                className="modern-home-card"
              >
                <AttendancePane
                  presenceLogs={runTour ? fakeData.presenceLogs : presenceLogs}
                  groups={groups}
                  selectedGroupForAttendance={selectedGroupForAttendance}
                  setSelectedGroupForAttendance={setSelectedGroupForAttendance}
                />
              </DashboardCard>
              <DashboardCard
                title={"נתוני על"}
                description={"המספרים החשובים של הארגון"}
                flex={1}
                data-tour="stats-pane"
                onClick={() => setActiveModal("stats")}
                className="modern-home-card"
              >
                <div className="stats-grid" style={{ gap: "8px" }}>
                  <div className="stat-item" style={{ padding: "8px" }}>
                    <span className="stat-label" style={{ fontSize: "1rem" }}>
                      ספורטאים
                    </span>
                    <strong
                      className="important-number"
                      style={{ fontSize: "1.2em" }}
                    >
                      <CountUp start={0} end={numberOfStudents} duration={2} />
                    </strong>
                  </div>
                  <div className="stat-item" style={{ padding: "8px" }}>
                    <span className="stat-label" style={{ fontSize: "1rem" }}>
                      קבוצות
                    </span>
                    <strong
                      className="important-number"
                      style={{ fontSize: "1.2em" }}
                    >
                      <CountUp start={0} end={numberOfGroups} duration={2} />
                    </strong>
                  </div>
                  <div className="stat-item" style={{ padding: "8px" }}>
                    <span className="stat-label" style={{ fontSize: "1rem" }}>
                      ספורטאים בקבוצה{"\n"}בממוצע
                    </span>
                    <strong
                      className="important-number"
                      style={{ fontSize: "1.2em" }}
                    >
                      <CountUp
                        start={0}
                        end={Math.round(numberOfStudents / numberOfGroups)}
                        duration={2}
                      />
                    </strong>
                  </div>
                  <div className="stat-item" style={{ padding: "8px" }}>
                    <span className="stat-label" style={{ fontSize: "1rem" }}>
                      אימונים שבוצעו החודש
                    </span>
                    <strong
                      className="important-number"
                      style={{ fontSize: "1.2em" }}
                    >
                      <CountUp
                        start={0}
                        end={numberOfTrainingInLastMonth}
                        duration={2}
                      />
                    </strong>
                  </div>
                </div>
              </DashboardCard>
            </div>
            <div className="pane-row-wrapper" style={{ marginBottom: "32px" }}>
              <DashboardCard
                title={"מגמת כמות ספורטאים בארגון"}
                description={"מגמת השינוי בחודשים האחרונים"}
                flex={1.5}
                data-tour="trend-pane"
                onClick={() => setActiveModal("trend")}
                className="modern-home-card"
              >
                <TrendPane
                  statsReports={runTour ? fakeData.statsReports : statsReports}
                  groups={groups}
                  isModal={false}
                />
              </DashboardCard>
              <DashboardCard
                title={"עדכונים"}
                description={"עדכונים חשובים של הארגון"}
                flex={1.5}
                data-tour="dynamic-updates-pane"
                className="modern-home-card"
              >
                <DynamicUpdatesPane
                  isModal={false}
                  students={students}
                  openBirthdayModal={openBirthdayModal}
                  waveringStudents={waveringStudents}
                  openWaveringModal={openWaveringModal}
                  attendanceMap={attendanceMap}
                  setCurrentTab={setCurrentTab}
                  events={events}
                  professionalContent={professionalContent}
                  openTrainingPlanModal={openTrainingPlanModal}
                />
              </DashboardCard>
            </div>
          </div>
        </div>
      </div>

      <HomeZoomPaneModal
        isOpen={activeModal === "groups"}
        onClose={() => setActiveModal(null)}
        title="ספורטאים בקבוצות"
        description="כמה ספורטאים יש בכל קבוצה"
      >
        <NumberOfStudentsInEachGroupPane
          groups={runTour ? fakeData.groups : groups}
          students={runTour ? fakeData.students : students}
          isModal={true}
        />
      </HomeZoomPaneModal>

      <HomeZoomPaneModal
        isOpen={activeModal === "stats"}
        onClose={() => setActiveModal(null)}
        title="נתוני על"
        description="המספרים החשובים של הארגון"
      >
        <div className="stats-grid" style={{ gap: "8px" }}>
          <div className="stat-item" style={{ padding: "8px", margin: "12px" }}>
            <span className="stat-label" style={{ fontSize: "2rem" }}>
              ספורטאים
            </span>
            <strong
              className="important-number"
              style={{ fontSize: "2rem", margin: "12px" }}
            >
              <CountUp start={0} end={numberOfStudents} duration={2} />
            </strong>
          </div>
          <div className="stat-item" style={{ padding: "8px", margin: "12px" }}>
            <span className="stat-label" style={{ fontSize: "2rem" }}>
              קבוצות
            </span>
            <strong className="important-number" style={{ fontSize: "2rem" }}>
              <CountUp start={0} end={numberOfGroups} duration={2} />
            </strong>
          </div>
          <div className="stat-item" style={{ padding: "8px", margin: "12px" }}>
            <span className="stat-label" style={{ fontSize: "2rem" }}>
              ספורטאים בקבוצה{"\n"}בממוצע
            </span>
            <strong className="important-number" style={{ fontSize: "2rem" }}>
              <CountUp
                start={0}
                end={Math.round(numberOfStudents / numberOfGroups)}
                duration={2}
              />
            </strong>
          </div>
          <div className="stat-item" style={{ padding: "8px", margin: "12px" }}>
            <span className="stat-label" style={{ fontSize: "2rem" }}>
              אימונים שבוצעו החודש
            </span>
            <strong className="important-number" style={{ fontSize: "2rem" }}>
              <CountUp
                start={0}
                end={numberOfTrainingInLastMonth}
                duration={2}
              />
            </strong>
          </div>
        </div>
      </HomeZoomPaneModal>

      <HomeZoomPaneModal
        isOpen={activeModal === "trend"}
        onClose={() => setActiveModal(null)}
        title="מגמת כמות ספורטאים בארגון"
        description="מגמת השינוי בחודשים האחרונים"
      >
        <TrendPane
          statsReports={runTour ? fakeData.statsReports : statsReports}
          groups={groups}
          isModal={true}
        />
      </HomeZoomPaneModal>

      <HomeZoomPaneModal
        isOpen={activeModal === "attendance"}
        onClose={() => setActiveModal(null)}
        title="נוכחות באימונים"
        description="כמה פעמים ספורטאים נעדרו מאימונים בחודש האחרון?"
      >
        <AttendancePane
          presenceLogs={runTour ? fakeData.presenceLogs : presenceLogs}
          groups={groups}
          selectedGroupForAttendance={selectedGroupForAttendance}
          setSelectedGroupForAttendance={setSelectedGroupForAttendance}
          isModal={true}
        />
      </HomeZoomPaneModal>

      <UXFeedbackInviteModal
        isOpen={showFeedbackInvite}
        onClose={handleFeedbackInviteClose}
        onAccept={handleFeedbackInviteAccept}
      />

      <UXFeedbackModal
        isOpen={showUXFeedback}
        onClose={() => setShowUXFeedback(false)}
        userId={userData?.userId}
      />

      {/* Birthday Students Modal */}
      <CustomModal
        isOpen={birthdayModalOpen}
        onClose={closeBirthdayModal}
        title="ימי הולדת בשבועיים הקרובים"
        width={90}
        height={90}
      >
        <div
          className="birthday-modal-content"
          style={{
            direction: "rtl",
            textAlign: "right",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            padding: "20px",
          }}
        >
          {getUpcomingBirthdays().length === 0 ? (
            <p className="no-birthdays">אין ספורטאים</p>
          ) : (
            getUpcomingBirthdays().map((student, index) => (
              <div
                key={index}
                className="birthday-modal-card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "20px",
                  width: "80%",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "10px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  className="birthday-modal-image"
                  style={{ marginLeft: "20px" }}
                >
                  <img
                    src={
                      student.imageURI && student.imageURI !== ""
                        ? student.imageURI
                        : "/default-avatar.png"
                    }
                    alt={`${student.firstName || ""} ${student.lastName || ""}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/default-avatar.png";
                    }}
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                </div>
                <div className="birthday-modal-details" style={{ flex: 1 }}>
                  <h4
                    className="student-name"
                    style={{
                      fontSize: "1.6em",
                      margin: "0 0 10px 0",
                      color: "#000000",
                      fontWeight: "bold",
                    }}
                  >
                    {student.name || `${student.firstName} ${student.lastName}`}
                    <span
                      style={{
                        fontSize: "0.6em",
                        color: "#000000",
                        display: "block",
                        fontWeight: "normal",
                      }}
                    >
                      {groups.find((g) => student.groupsIDs?.includes(g.id))
                        ?.name || ""}{" "}
                      -{" "}
                      {groups.find((g) => student.groupsIDs?.includes(g.id))
                        ?.dojo || ""}
                    </span>
                  </h4>
                  <div className="birthday-info" style={{ fontSize: "1.1em" }}>
                    <span
                      className="next-age"
                      style={{
                        color: "#0066CC",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      {student.gender === "זכר" ? "יהיה בן" : "תהיה בת"}{" "}
                      {getNextAge(student.birthday)} בעוד{" "}
                      {getDaysUntilBirthday(student.birthday)} ימים (
                      {student.birthday.toDate().toLocaleDateString("he-IL", {
                        month: "2-digit",
                        day: "2-digit",
                      })}
                      )
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CustomModal>

      {/* Wavering Students Modal */}
      <CustomModal
        isOpen={waveringModalOpen}
        onClose={closeWaveringModal}
        title="ספורטאים מתנדנדים בחודש האחרון"
        width={90}
        height={90}
      >
        <div
          className="birthday-modal-content"
          style={{
            direction: "rtl",
            textAlign: "right",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            padding: "20px",
          }}
        >
          {waveringStudents.length === 0 ? (
            <p className="no-birthdays">אין ספורטאים</p>
          ) : (
            waveringStudents.map((student, index) => {
              const attendance = attendanceMap[student.id];
              const missedPercentage = attendance
                ? Math.round(
                  ((attendance.total - attendance.present) /
                    attendance.total) *
                  100
                )
                : 0;

              return (
                <div
                  key={index}
                  className="birthday-modal-card"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "20px",
                    width: "80%",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "10px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <div
                    className="birthday-modal-image"
                    style={{ marginLeft: "20px" }}
                  >
                    <img
                      src={
                        student.imageURI && student.imageURI !== ""
                          ? student.imageURI
                          : "/default-avatar.png"
                      }
                      alt={`${student.firstName || ""} ${student.lastName || ""
                        }`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default-avatar.png";
                      }}
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <div className="birthday-modal-details" style={{ flex: 1 }}>
                    <h4
                      className="student-name"
                      style={{
                        fontSize: "1.6em",
                        margin: "0 0 10px 0",
                        color: "#000000",
                        fontWeight: "bold",
                      }}
                    >
                      {student.name ||
                        `${student.firstName} ${student.lastName}`}
                      <span
                        style={{
                          fontSize: "0.6em",
                          color: "#000000",
                          display: "block",
                          fontWeight: "normal",
                        }}
                      >
                        {groups.find((g) => student.groupsIDs?.includes(g.id))
                          ?.name || ""}{" "}
                        -{" "}
                        {groups.find((g) => student.groupsIDs?.includes(g.id))
                          ?.dojo || ""}
                      </span>
                    </h4>
                    <div
                      className="birthday-info"
                      style={{ fontSize: "1.1em" }}
                    >
                      <span
                        className="next-age"
                        style={{
                          color: "#0066CC",
                          display: "block",
                        }}
                      >
                        {student.gender === "נקבה" ? "החסירה" : "החסיר"}{" "}
                        {missedPercentage}% מהאימונים
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CustomModal>

      {/* Training Plan Modal */}
      <CustomModal
        isOpen={trainingPlanModalOpen}
        onClose={closeTrainingPlanModal}
        title="תוכנית אימונים - חודש נוכחי"
        width={90}
        height={90}
      >
        <div
          style={{
            direction: "rtl",
            textAlign: "right",
            padding: "20px",
          }}
        >
          {(() => {
            const { monthData, weekData, month, week } =
              getCurrentMonthAndWeek();
            const monthName = getMonthName(month);

            return (
              <>
                <div style={{ marginBottom: "30px" }}>
                  <h3 style={{ color: "#0066CC", marginBottom: "15px" }}>
                    חודש {monthName}
                  </h3>
                  {monthData?.title && (
                    <h4 style={{ marginBottom: "10px" }}>{monthData.title}</h4>
                  )}
                  {monthData?.description && (
                    <p style={{ lineHeight: "1.6", color: "#333" }}>
                      {monthData.description}
                    </p>
                  )}
                  {!monthData?.title && !monthData?.description && (
                    <p style={{ color: "#666", fontStyle: "italic" }}>
                      אין תוכן זמין לחודש זה
                    </p>
                  )}
                </div>

                <div>
                  <h3 style={{ color: "#0066CC", marginBottom: "15px" }}>
                    שבוע {week}
                  </h3>
                  {weekData?.title && (
                    <h4 style={{ marginBottom: "10px" }}>{weekData.title}</h4>
                  )}
                  {weekData?.description && (
                    <p style={{ lineHeight: "1.6", color: "#333" }}>
                      {weekData.description}
                    </p>
                  )}
                  {!weekData?.title && !weekData?.description && (
                    <p style={{ color: "#666", fontStyle: "italic" }}>
                      אין תוכן זמין לשבוע זה
                    </p>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </CustomModal>
    </>
  );
}

export default React.memo(HomePane);
